import { Checkbox, FormControlLabel, Stack } from '@chronosphereio/chrono-ui';
import { produce } from 'immer';
import { merge } from 'lodash';
import { DistributionChartSpec, UNIT_SETTINGS_NAME, UnitOptions } from '@/core';
import {
  OptionsEditorColumn,
  OptionsEditorGrid,
  OptionsEditorGroup,
  UnitSelector,
  UnitSelectorProps,
} from '@/plugin-editor-components';
import { DEFAULT_UNIT, DistributionChartOptionsEditorProps } from './distribution-chart-model';

export function DistributionChartOptionsEditorSettings(props: DistributionChartOptionsEditorProps) {
  const { onChange, value } = props;

  const handleUnitChange: UnitSelectorProps['onChange'] = (newUnit: UnitOptions) => {
    onChange(
      produce(value, (draft: DistributionChartSpec) => {
        draft.unit = newUnit;
      })
    );
  };

  const handlePercentilesChange: PercentilesSelectorProps['onChange'] = (percentiles: number[]) => {
    onChange(
      produce(value, (draft: DistributionChartSpec) => {
        draft.percentile_markers = percentiles;

        if (percentiles.length === 0) {
          delete draft.percentile_markers;
        }
      })
    );
  };

  // ensures decimal_places defaults to correct value
  const unit = merge({}, DEFAULT_UNIT, value.unit);

  return (
    <OptionsEditorGrid>
      <OptionsEditorColumn>
        <OptionsEditorGroup title={UNIT_SETTINGS_NAME}>
          <UnitSelector value={unit} onChange={handleUnitChange} />
        </OptionsEditorGroup>
      </OptionsEditorColumn>

      <OptionsEditorColumn>
        <OptionsEditorGroup title={'Percentile  Markers'}>
          <PercentilesSelector onChange={handlePercentilesChange} percentile_markers={value.percentile_markers ?? []} />
        </OptionsEditorGroup>
      </OptionsEditorColumn>
    </OptionsEditorGrid>
  );
}

interface PercentilesSelectorProps {
  onChange: (percentile_markers: number[]) => void;
  percentile_markers: number[];
}

const percentileChangeHandler = (
  percentile: number,
  onAdd: (percentile: number) => void,
  onRemove: (percentile: number) => void
) => {
  return (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (checked) {
      onAdd(percentile);
    } else {
      onRemove(percentile);
    }
  };
};

const PercentilesSelector = (props: PercentilesSelectorProps) => {
  const { onChange, percentile_markers } = props;
  const onAdd = (percentile: number) => {
    if (!percentile_markers.includes(percentile)) {
      const newPercentiles = [...percentile_markers, percentile];
      newPercentiles.sort();
      onChange(newPercentiles);
    }
  };

  const onRemove = (percentile: number) => {
    if (percentile_markers.includes(percentile)) {
      const newPercentiles = percentile_markers.filter((p) => p !== percentile);
      onChange(newPercentiles);
    }
  };

  return (
    <Stack direction="column">
      <FormControlLabel
        control={
          <Checkbox checked={percentile_markers.includes(50)} onChange={percentileChangeHandler(50, onAdd, onRemove)} />
        }
        label="P50"
      />
      <FormControlLabel
        control={
          <Checkbox checked={percentile_markers.includes(75)} onChange={percentileChangeHandler(75, onAdd, onRemove)} />
        }
        label="P75"
      />
      <FormControlLabel
        control={
          <Checkbox checked={percentile_markers.includes(90)} onChange={percentileChangeHandler(90, onAdd, onRemove)} />
        }
        label="P90"
      />
      <FormControlLabel
        control={
          <Checkbox checked={percentile_markers.includes(95)} onChange={percentileChangeHandler(95, onAdd, onRemove)} />
        }
        label="P95"
      />
      <FormControlLabel
        control={
          <Checkbox checked={percentile_markers.includes(99)} onChange={percentileChangeHandler(99, onAdd, onRemove)} />
        }
        label="P99"
      />
    </Stack>
  );
};
